import React from 'react';
import { useTranslation } from 'react-i18next';
import { formStyles } from '../utils/formStyles';
import { FileUploadButton } from '../components/FileUploadButton';
import { XMarkIcon } from '@heroicons/react/24/outline';

const API_URL = import.meta.env.VITE_API_URL;
const getImageUrl = (image) => {
  if (!image) return null;
  if (typeof image === 'string') {
    return image.startsWith('http')
      ? image
      : `${API_URL.replace(/\/$/, '')}/${image.replace(/^\//, '')}`;
  }
  return URL.createObjectURL(image);
};

export function HomeForm({ 
  formData, 
  handleChange, 
  handleImageChange, 
  handleSubmit, 
  isEdit = false 
}) {
  const { t } = useTranslation();
  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="mb-2">
        <h2 className="text-2xl font-bold mb-1 text-gray-900 dark:text-white">
          {t('homeform.section_title', 'Home-Seite bearbeiten')}
        </h2>
        <p className="text-gray-600 dark:text-gray-300">
          {t('homeform.section_desc', 'Bearbeite alle Felder und speichere am Ende einmal alles zusammen.')}
        </p>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex gap-3">
          <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="text-sm text-blue-800 dark:text-blue-200">
            <p className="font-medium">{t('homeform.info_title', 'Hinweis zum Speichern:')}</p>
            <p className="mt-1 text-blue-700 dark:text-blue-300">
              {t('homeform.info_text', 'Änderungen werden erst übernommen, wenn du unten auf "Gesamte Home-Seite aktualisieren" klickst. Du kannst beliebig viele Felder bearbeiten und dann alles auf einmal speichern.')}
            </p>
          </div>
        </div>
      </div>

      {/* Nav Subtitles */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="relative">
          <input
            type="text"
            id="nav_subtitle_de"
            name="nav_subtitle_de"
            value={formData.nav_subtitle_de || ''}
            onChange={handleChange}
            className={formStyles.input}
          />
          <label htmlFor="nav_subtitle_de" className={`${formStyles.label} ${formData.nav_subtitle_de ? formStyles.labelActive : ''}`}>
            {t('homeform.nav_subtitle_de', 'Nav Subtitle (German)')}
          </label>
        </div>
        <div className="relative">
          <input
            type="text"
            id="nav_subtitle_en"
            name="nav_subtitle_en"
            value={formData.nav_subtitle_en || ''}
            onChange={handleChange}
            className={formStyles.input}
          />
          <label htmlFor="nav_subtitle_en" className={`${formStyles.label} ${formData.nav_subtitle_en ? formStyles.labelActive : ''}`}>
            {t('homeform.nav_subtitle_en', 'Nav Subtitle (English)')}
          </label>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="relative">
          <input
            type="text"
            id="title_de"
            name="title_de"
            value={formData.title_de}
            onChange={handleChange}
            className={formStyles.input}
            required
          />
          <label htmlFor="title_de" className={`${formStyles.label} ${formData.title_de ? formStyles.labelActive : ''}`}>
            {t('homeform.title_de', 'Title (German)')}
          </label>
        </div>

        <div className="relative">
          <input
            type="text"
            id="title_en"
            name="title_en"
            value={formData.title_en}
            onChange={handleChange}
            className={formStyles.input}
            required
          />
          <label htmlFor="title_en" className={`${formStyles.label} ${formData.title_en ? formStyles.labelActive : ''}`}>
            {t('homeform.title_en', 'Title (English)')}
          </label>
        </div>
      </div>

      {/* Hero Subtitles */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="relative">
          <input
            type="text"
            id="hero_subtitle_de"
            name="hero_subtitle_de"
            value={formData.hero_subtitle_de || ''}
            onChange={handleChange}
            className={formStyles.input}
          />
          <label htmlFor="hero_subtitle_de" className={`${formStyles.label} ${formData.hero_subtitle_de ? formStyles.labelActive : ''}`}>
            {t('homeform.hero_subtitle_de', 'Hero Subtitle (German)')}
          </label>
        </div>
        <div className="relative">
          <input
            type="text"
            id="hero_subtitle_en"
            name="hero_subtitle_en"
            value={formData.hero_subtitle_en || ''}
            onChange={handleChange}
            className={formStyles.input}
          />
          <label htmlFor="hero_subtitle_en" className={`${formStyles.label} ${formData.hero_subtitle_en ? formStyles.labelActive : ''}`}>
            {t('homeform.hero_subtitle_en', 'Hero Subtitle (English)')}
          </label>
        </div>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Hero Image */}
          <div>
            <label className="block mb-2 font-semibold text-gray-700 dark:text-gray-200">{t('homeform.hero_image', 'Hero Image')}</label>
            <FileUploadButton
              id="hero_image"
              accept="image/*"
              onChange={e => handleImageChange(e, 'hero_image')}
              label={t('homeform.upload_hero_image', 'Upload Hero Image')}
              className={formStyles.fileUploadButton}
            />
            {formData.hero_image && (
              <div className="relative mt-2 group w-full max-w-xs">
                <img
                  src={getImageUrl(formData.hero_image)}
                  alt="Hero Preview"
                  className="w-full h-40 object-cover rounded-lg border-2 border-gray-200 dark:border-zinc-700 shadow-sm"
                />
                <button
                  type="button"
                  onClick={() => handleChange({ target: { name: 'hero_image', value: null } })}
                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 shadow hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-400 opacity-90 group-hover:opacity-100 transition-opacity"
                  aria-label={t('homeform.remove_hero_image', 'Remove hero image')}
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>
          {/* About Image */}
          <div>
            <label className="block mb-2 font-semibold text-gray-700 dark:text-gray-200">{t('homeform.about_image', 'About Image')}</label>
            <FileUploadButton
              id="about_image"
              accept="image/*"
              onChange={e => handleImageChange(e, 'about_image')}
              label={t('homeform.upload_about_image', 'Upload About Image')}
              className={formStyles.fileUploadButton}
            />
            {formData.about_image && (
              <div className="relative mt-2 group w-full max-w-xs">
                <img
                  src={getImageUrl(formData.about_image)}
                  alt="About Preview"
                  className="w-full h-40 object-cover rounded-lg border-2 border-gray-200 dark:border-zinc-700 shadow-sm"
                />
                <button
                  type="button"
                  onClick={() => handleChange({ target: { name: 'about_image', value: null } })}
                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 shadow hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-400 opacity-90 group-hover:opacity-100 transition-opacity"
                  aria-label={t('homeform.remove_about_image', 'Remove about image')}
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="relative">
          <textarea
            id="description_de"
            name="description_de"
            value={formData.description_de}
            onChange={handleChange}
            className={`${formStyles.input} min-h-[100px]`}
            required
          />
          <label htmlFor="description_de" className={`${formStyles.label} ${formData.description_de ? formStyles.labelActive : ''}`}>
            {t('homeform.description_de', 'Description (German)')}
          </label>
        </div>

        <div className="relative">
          <textarea
            id="description_en"
            name="description_en"
            value={formData.description_en}
            onChange={handleChange}
            className={`${formStyles.input} min-h-[100px]`}
            required
          />
          <label htmlFor="description_en" className={`${formStyles.label} ${formData.description_en ? formStyles.labelActive : ''}`}>
            {t('homeform.description_en', 'Description (English)')}
          </label>
        </div>
      </div>

      {/* About Description 2 */}
      <div className="space-y-4">
        <div className="relative">
          <textarea
            id="about_description2_de"
            name="about_description2_de"
            value={formData.about_description2_de || ''}
            onChange={handleChange}
            className={`${formStyles.input} min-h-[100px]`}
          />
          <label htmlFor="about_description2_de" className={`${formStyles.label} ${formData.about_description2_de ? formStyles.labelActive : ''}`}>
            {t('homeform.about_description2_de', 'About Description 2 (German)')}
          </label>
        </div>
        <div className="relative">
          <textarea
            id="about_description2_en"
            name="about_description2_en"
            value={formData.about_description2_en || ''}
            onChange={handleChange}
            className={`${formStyles.input} min-h-[100px]`}
          />
          <label htmlFor="about_description2_en" className={`${formStyles.label} ${formData.about_description2_en ? formStyles.labelActive : ''}`}>
            {t('homeform.about_description2_en', 'About Description 2 (English)')}
          </label>
        </div>
      </div>

      <div className="mt-8 pt-6 border-t border-gray-200 dark:border-zinc-700">
        <button type="submit" className={`${formStyles.button} flex items-center gap-2`}>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V2" />
          </svg>
          {isEdit ? t('homeform.update', 'Gesamte Home-Seite aktualisieren') : t('homeform.create', 'Home-Seite erstellen')}
        </button>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
          {t('homeform.save_hint', 'Speichert alle Texte, Bilder und Einstellungen auf einmal')}
        </p>
      </div>
    </form>
  );
} 